<template>
  <div class="pa-4">
    <!-- Affichage d'une barre de progression pendant le chargement des données -->
    <load-progress :condition="!dataLoaded" :label="'Chargement des données'">
    </load-progress>
    <!-- Affichage du tree view et des éditeurs selon l'objet sélectionné -->
    <div v-if="dataLoaded">
      <div>
        <div class="dashboard-header d-flex align-center justify-space-between pb-10 pt-4">
          <div class="d-flex align-center">
            <img src="@/assets/SVG/Step1Image.svg" class="dashboard-logo"/>

            <div class="ms-4">
              <p class="m-0 dashboard-welcome">
                Bonjour
                <span class="prenom-gradient">{{ formattedPrenom }}</span>
                !
              </p>

              <p class="m-0 dashboard-date">
                Nous sommes le {{ todayLong }}
              </p>
            </div>
          </div>

          <div v-if="isClient" class="d-flex align-center ga-3">
            <loader-button
                v-if="canRelancerMigration"
                label="Relancer migration"
                :loading="migrationInProgress"
                @callback="relancerMigration"
            />
            <loader-button
                :label="headerButtonLabel"
                @callback="goToProjet"
            />
          </div>
        </div>

        <div v-if="isClient" class="dashboard-grid">
          <WidgetRenderer
              v-for="widget in sortedWidgets"
              :key="widget.id"
              :widget="widget"
          />
        </div>

        <div v-else>
          <div>
            <v-row class="m-0 pt-0">
              <v-col class="col-6">
                <v-row class="pb-3" v-for="(module, index) in modules" :key="index">
                  <module-card
                      v-if="index % 2 === 0"
                      :module="module"
                      :small="small(index)"
                      :picto="picto(index)"
                      :height="height(index)"
                      :htmlProps="htmlProps(index)"
                      :htmlClick="(dimensions) => htmlClick(index, dimensions)"
                      :hasTitle="hasTitle(index)"
                  />
                </v-row>
              </v-col>

              <v-col class="col-6 pt-0">
                <v-row class="pb-3" v-for="(module, index) in modules" :key="index">
                  <module-card
                      v-if="index % 2 === 1"
                      :module="module"
                      :small="small(index)"
                      :picto="picto(index)"
                      :height="height(index)"
                      :htmlProps="htmlProps(index)"
                      :htmlClick="(dimensions) => htmlClick(index, dimensions)"
                      :hasTitle="hasTitle(index)"
                  />
                </v-row>
              </v-col>
            </v-row>
          </div>
        </div>

        <section-container :dialogOpen="showWizard"
                           :section="section"
                           @closeDialog="hideWizard">
        </section-container>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import LoadProgress from '@/framework/base/components/LoadProgress.vue';
import {storeEtablissement, storeProjet, storeSession} from '@/generated/stores';
import ModuleCard from '@/framework/module/components/ModuleCard.vue';
import Module from '@/generated/classes/Module';
import {TitleCase} from '@/framework/base/helpers/String';
import {Fiches, Modules} from '@/generated/classes/AppSchema';
import {goToModule} from '@/framework/base/helpers/NavHelper';
import {Position} from '@/framework/base/helpers/ElementPosition';
import {AppModules, RessourceAppModule} from '@/ressources/globalRessources';
import {isMobile} from '@/style/StyleHelpers';
import SectionContainer from '@/framework/base/components/SectionContainer.vue';
import Section from '@/framework/base/classes/Section';
import Wizard from '@/generated/classes/Wizard';
import {ListFilter} from '@/extend/classes/ListData';
import User from '@/generated/classes/User';
import UserProfile, {EnumProfile} from '@/framework/base/classes/UserProfile';
import {defineAsyncComponent, defineComponent, markRaw} from "vue";
import SectionWizard from '@/framework/wizard/components/SectionWizard.vue';
import {useRouter} from "vue-router";
import SectionHeader from "@/framework/base/components/SectionHeader.vue";
import {MapRessource} from "@/framework/base/helpers/Ressources.ts";
import DashboardWidget, {DashboardWidgetWidgetTypes} from "@/generated/classes/DashboardWidget.ts";
import LoaderButton from "@/framework/base/components/LoaderButton.vue";
import WidgetRenderer from "@/framework/dashboard/components/WidgetRenderer.vue";
import Route from "@/extend/classes/Route.ts";
import {getSection} from "@/framework/base/helpers/ComponentFinder.ts";
import Synchro98 from '@/extend/classes/Synchro98';

export default defineComponent({
  name: 'DashboardComponent',
  components: {
    WidgetRenderer,
    LoaderButton,
    SectionHeader,
    ModuleCard,
    LoadProgress,
    SectionContainer,
  },
  data() {
    const modules: Module[] = [];

    return {
      modules,
      modulesLoaded: false,
      isMobile: isMobile(),
      showWizard: false,
      wizard: new Wizard(),
      router: useRouter(),
      synchroDoc: false,
      migrationInProgress: false,
      dashboardWidgets: [] as DashboardWidget[],
    };
  },
  mounted() {
    this.loadDashboardWidgets()
  },
  computed: {
    dataLoaded(): boolean {
      if (!storeSession.sessionValid && !storeSession.loginOut) {
        return true;
      }
      if (!this.modulesLoaded) {
        this.getModules();
        const user = storeSession.connectedUser
        if (user && user.firstConnexion) {
          user.firstConnexion = false;
        }
        return false;
      }
      return true;
    },
    user(): User | null {
      if (storeSession.sessionValid) {
        const user = storeSession.connectedUser;
        if (user != undefined) {
          return user;
        }
      }
      return null
    },
    isClient(): boolean {
      return this.user !== null &&
          (this.user.currentProfil & EnumProfile.client) > 0;
      // return false;
    },
    migratedEtablissementId(): number {
      if (!this.user || !storeSession.isSudo || !this.isClient || this.user.clientId <= 0) {
        return 0;
      }
      const clientId = this.user.clientId;
      const migrated = storeEtablissement.listEtablissement.filter(etablissement =>
          etablissement.clientId === clientId &&
          etablissement.id > 0 && etablissement.id < 600000);
      return migrated.length === 1 ? migrated[0].id : 0;
    },
    canRelancerMigration(): boolean {
      const adminPrenom = sessionStorage.getItem('resynchSudoAdminPrenom');
      return this.migratedEtablissementId > 0 &&
          sessionStorage.getItem('resynchSudoToken') === storeSession.httpToken &&
          (sessionStorage.getItem('resynchSudoAdminNom') ?? '').includes('Gevigney') &&
          (adminPrenom === 'Christophe' || adminPrenom === 'Hervé');
    },
    section(): Section {
      const section = new Section();
      section.component = SectionWizard;
      section.data = this.wizard;
      section.helper = true;
      return section;
    },
    headerButtonLabel(): string {
      return "Nouveau projet de paie";
    },
    formattedPrenom() {
      if (!this.user) return '';
      const p = this.user.prenom || '';
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    },
    todayLong() {
      const now = new Date();
      return now.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    },
    imagePath(): string {
      return "../../src/assets/SVG/Step1Image.svg";
    },
    sortedWidgets() {
      if (!this.dashboardWidgets) return []

      return [...this.dashboardWidgets]
          .sort((a, b) => {
            const rowA = a.widgetMeta?.row || 0
            const rowB = b.widgetMeta?.row || 0

            if (rowA !== rowB) {
              return rowA - rowB
            }

            const colA = a.widgetMeta?.col || 0
            const colB = b.widgetMeta?.col || 0

            if (colA !== colB) {
              return colA - colB
            }

            const orderA = a.widgetMeta?.order || 0
            const orderB = b.widgetMeta?.order || 0

            return orderA - orderB
          })
    }
  },
  created(): void {
    Wizard.showWizard(Modules.NONE).then((res) => {
      if (res.id > 0) {
        this.wizard = res;
        this.showWizard = true;
      } else {
        User.acceptCgu(true);
      }
    })
    if (storeSession.isSudo) {
      const allData = new ListFilter();
      allData.pageLimit = -1;
      storeEtablissement.registerEtablissement(allData);
    }
    const user = storeSession.connectedUser
    if (user && user.firstConnexion) {
      //this.synchroDoc = user.firstConnexion;
    }
  },
  methods: {
    small(index: number): boolean {
      return Math.trunc((index + 1) / 2) % 2 == 0;
    },
    picto(index: number): string {
      return 'ModulePicto' + TitleCase(this.modules[index].route.module);
    },
    height(index: number): number | null {
      if (this.modules[index].route.module == Modules.PROFIL) {
        return null;
      }
      return 200;
    },
    hasTitle(index: number): boolean {
      let res = false
      if (this.modules[index].route.module == Modules.PROFIL) {
        res = false;
      } else {
        res = true;
      }
      return res;
    },
    htmlProps(index: number): any {
      const user = storeSession.connectedUser;
      if (this.modules[index].route.module == Modules.PROFIL && user != null) {
        return {
          nomPrenom: user.prenom + ' ' + user.nom,
          isEmployeur: UserProfile.hasProfile(user, EnumProfile.client),
          isSalarie: UserProfile.hasProfile(user, EnumProfile.salarie),
          employeur: "Mon compte employeur",
          salarie: "Mon compte salarié"
        };
      } else if (this.modules[index].route.module == Modules.PROJET) {
        return {
          title: "Mon compte employeur",
          autre: "Mon compte salarié",
          subtitle: RessourceAppModule(AppModules.PROJETS).label
        };
      }
      return null;
    },
    htmlClick(module: Module, dimensions: Position) {
      if (event != undefined) {
        event.stopPropagation();
      }
      goToModule(this.router, module.route, dimensions);
    },
    getModules(): void {
      this.modules = [];
      Module.list(this.isMobile).then((res: Module[]) => {
        res.forEach((t) => {
          if (t.inDashboard) {
            if (t.route.module == Modules.ADMIN) {
              if (!this.isMobile) {
                this.modules.push(t)
              }
            } else {
              this.modules.push(t)
            }
          }
        });
        this.modulesLoaded = true;
      });
    },
    goToProjet(): void {
      const obj = storeProjet.createNewProjet();
      const route = new Route();
      route.module = Modules.PROJET;
      route.fiche = Fiches.PROJET;
      route.ficheObjId = obj.id;
      goToModule(this.router, route);
    },
    async relancerMigration(): Promise<void> {
      const etablissementId = this.migratedEtablissementId;
      if (etablissementId <= 0 || this.migrationInProgress || !storeSession.httpCx) {
        return;
      }
      this.migrationInProgress = true;
      try {
        await Synchro98.resynch(etablissementId);
      } catch {
        // Les erreurs HTTP sont traitées par l'intercepteur global.
      } finally {
        this.migrationInProgress = false;
      }
    },

    goToListContrat(): void {
      const section  = new Section();
      const loader = getSection("SectionContratListExtend");
      const route = new Route();
      route.module = Modules.PROJET;
      route.fiche = Fiches.CONTAINER;
      let component = null;
      if (loader) {
        component = markRaw(defineAsyncComponent(loader as any));
      }
      if (component) {
        section.component = component;
        section.fullscreen = true;
      }
      route.data = section;


      goToModule(this.router, route);
    },
    hideWizard() {
      User.acceptCgu(true);
      this.showWizard = false;
    },
    loadDashboardWidgets() {
      DashboardWidget.load().then(res => {
        this.dashboardWidgets = res;
      });
    }
  }
})

</script>

<style scoped>
.dashboard-logo {
  width: 100px;
  object-fit: contain;
}

.dashboard-welcome {
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
}

.prenom-gradient {
  background: linear-gradient(91deg, rgb(var(--v-theme-primary70)) 28.33%, rgb(var(--v-theme-primary50)) 76.57%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
}

.dashboard-date {
  font-size: 16px;
  font-weight: 400;
  color: rgb(var(--v-theme-secondary20));
  margin-top: 4px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-auto-rows: auto;
  gap: 16px;
}
</style>
